// Payouts > Receivables (Paystack plan 5.8.2, 8.5, PR 14): money an operator
// may owe Zuttle back, after a refund (or a lost dispute) of a payment
// Paystack had paid to the operator at source. Read only. The filters come
// from the backend's own list of kinds, so a new reason, lane or status shows
// here without a change. A void receivable is in no total.

import React, { useCallback, useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import { AdminReceivableAPI } from '../../services/api';
import { rands, fmtDate } from './money';

const LABELS = {
  reason: { refund: 'Refund', dispute: 'Lost dispute' },
  lane: { ride: 'On-demand', prebooking: 'Pre-booking', seat: 'Seat', tip: 'Tip', charter: 'Charter' },
  status: { open: 'Open', recovered: 'Recovered', written_off: 'Written off', void: 'Void (the refund failed)' },
};
const label = (kind, value) => LABELS[kind][value] || value;
const TONE = { open: 'bg-yellow-50 text-yellow-700', recovered: 'bg-green-50 text-green-700', written_off: 'bg-gray-100 text-gray-700', void: 'bg-gray-50 text-gray-400' };

const Select = ({ id, text, value, onChange, options }) => (
  <label htmlFor={id} className="text-sm text-gray-600">
    {text}
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
      className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white">
      {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
    </select>
  </label>
);

const ReceivablesList = () => {
  const [filters, setFilters] = useState({ status: 'open', reason: '', lane: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The newest 200 (the most the backend gives at once); the totals cover all of them.
      const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), limit: 200 };
      const res = await AdminReceivableAPI.list(params);
      if (res.data.success) setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load the receivables');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const kinds = data?.kinds || { reasons: [], lanes: [], statuses: [] };
  const counted = kinds.statuses.filter((s) => s !== 'void');
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const rows = data?.receivables || [];

  return (
    <div>
      <p className="text-gray-600 mb-4">
        When a payment Paystack paid to the operator at source is refunded, or a dispute on it is lost, the operator may owe Zuttle its share back.
        A void receivable (its refund failed) is in no total.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {counted.map((s) => (
          <div key={s} className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">{label('status', s)}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{rands(data?.totals?.[s] || 0)}</p>
          </div>
        ))}
      </div>

      {data?.byCompany?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Per operator</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 font-medium">Operator</th>
                {counted.map((s) => <th key={s} className="py-2 font-medium text-right">{label('status', s)}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.byCompany.map((c) => (
                <tr key={c.companyId}>
                  <td className="py-2 text-gray-900">{c.companyName}</td>
                  {counted.map((s) => <td key={s} className="py-2 text-right text-gray-700">{rands(c[s] || 0)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-4">
        <Select id="receivable-status" text="Status" value={filters.status} onChange={set('status')}
          options={[...kinds.statuses.map((s) => [s, label('status', s)]), ['all', 'All']]} />
        <Select id="receivable-reason" text="Reason" value={filters.reason} onChange={set('reason')}
          options={[['', 'All'], ...kinds.reasons.map((r) => [r, label('reason', r)])]} />
        <Select id="receivable-lane" text="Lane" value={filters.lane} onChange={set('lane')}
          options={[['', 'All'], ...kinds.lanes.map((l) => [l, label('lane', l)])]} />
      </div>

      {error ? (
        <div className="text-red-600 text-sm">{error}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Receipt} title="No receivables" message="No receivable matches these filters." />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="p-3 font-medium">Opened</th>
                <th className="p-3 font-medium">Operator</th>
                <th className="p-3 font-medium">Reason</th>
                <th className="p-3 font-medium">Lane</th>
                <th className="p-3 font-medium text-right">Amount</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="p-3 text-gray-600 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                  <td className="p-3 text-gray-900">{r.companyName}</td>
                  <td className="p-3 text-gray-600">{label('reason', r.reason)}</td>
                  <td className="p-3 text-gray-600">{label('lane', r.lane)}</td>
                  <td className={`p-3 text-right font-medium ${r.status === 'void' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{rands(r.amountCents)}</td>
                  <td className="p-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TONE[r.status] || 'bg-gray-100 text-gray-700'}`}>{label('status', r.status)}</span>
                  </td>
                  <td className="p-3 text-gray-500">
                    <span className="font-mono text-xs">{r.sourceRef}</span>
                    {r.note && <p className="text-xs text-gray-400 mt-0.5">{r.note}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data?.pagination?.pages > 1 && (
            <p className="text-xs text-gray-400 p-3">Showing the newest {rows.length} of {data.pagination.total}.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ReceivablesList;
